import { frontendURL } from 'dashboard/helper/URLHelper.js';

import CampaignsPageRouteView from './pages/CampaignsPageRouteView.vue';
import LiveChatCampaignsPage from './pages/LiveChatCampaignsPage.vue';
import SMSCampaignsPage from './pages/SMSCampaignsPage.vue';
import WhatsAppCampaignsPage from './pages/WhatsAppCampaignsPage.vue';
import WhatsAppCampaignAnalyticsPage from './pages/WhatsAppCampaignAnalyticsPage.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

/* >>> WASLO DEVIATION >>> keep on upstream bump; see pages/broadcasts/nav.js.
   Broadcasts left the Waslo AI section and became a child of this group: it is outbound
   marketing that costs money per message, it shares no engine with authoring an assistant,
   and it was the one tab in the old seven-tab UI that had nothing to do with the other six.
   THE PLAN, §2. */
import BroadcastsIndex from './pages/broadcasts/BroadcastsIndex.vue';
import BroadcastCompose from './pages/broadcasts/BroadcastCompose.vue';
import BroadcastDetail from './pages/broadcasts/BroadcastDetail.vue';
import { wasloProvisionedGuard } from '../wasloai/provisioning-guard.js';
/* <<< WASLO DEVIATION <<< */

const meta = {
  featureFlag: FEATURE_FLAGS.CAMPAIGNS,
  permissions: ['administrator'],
};

/* >>> WASLO DEVIATION >>> keep on upstream bump.
   NO `featureFlag`, unlike the `meta` above. `usePolicy.js` returns true for a falsy flag,
   so leaving it unset needs no plumbing — and a flag is exactly why Captain renders for
   nobody on this community install. `permissions: ['administrator']` is the single gate,
   read by the router guard to admit the navigation AND by `provider.js` to decide whether
   the sidebar entry renders at all. One declaration, two effects, no drift. */
const wasloMeta = { permissions: ['administrator'] };
/* <<< WASLO DEVIATION <<< */

const campaignsRoutes = {
  routes: [
    {
      path: frontendURL('accounts/:accountId/campaigns'),
      component: CampaignsPageRouteView,
      children: [
        {
          path: '',
          redirect: to => {
            return { name: 'campaigns_ongoing_index', params: to.params };
          },
        },
        {
          path: 'ongoing',
          name: 'campaigns_ongoing_index',
          meta,
          redirect: to => {
            return { name: 'campaigns_livechat_index', params: to.params };
          },
        },
        {
          path: 'one_off',
          name: 'campaigns_one_off_index',
          meta,
          redirect: to => {
            return { name: 'campaigns_sms_index', params: to.params };
          },
        },
        {
          path: 'live_chat',
          name: 'campaigns_livechat_index',
          meta,
          component: LiveChatCampaignsPage,
        },
        {
          path: 'sms',
          name: 'campaigns_sms_index',
          meta,
          component: SMSCampaignsPage,
        },
        {
          path: 'whatsapp',
          name: 'campaigns_whatsapp_index',
          meta: {
            ...meta,
            featureFlag: FEATURE_FLAGS.WHATSAPP_CAMPAIGNS,
          },
          component: WhatsAppCampaignsPage,
        },
        {
          path: 'whatsapp/:campaignId/analytics',
          name: 'campaigns_whatsapp_analytics',
          meta: {
            ...meta,
            featureFlag: FEATURE_FLAGS.WHATSAPP_CAMPAIGNS,
          },
          component: WhatsAppCampaignAnalyticsPage,
        },
        /* >>> WASLO DEVIATION >>> keep on upstream bump; THE PLAN, §2 and §4 P10.
           Three real routes, not one route with a step counter in a ref: `new?step=<id>`
           makes every step of the compose sequence a URL, so the browser's own Back button
           walks it backwards and a step can be bookmarked. `new` is declared BEFORE
           `:broadcastId` for readability only — vue-router ranks static segments above
           dynamic ones, so the order cannot decide it either way. */
        {
          path: 'broadcasts',
          name: 'waslo_broadcasts',
          meta: wasloMeta,
          // Same gate as the Waslo AI section, declared per route because these three are
          // children of a group Chatwoot owns — there is no Waslo parent record here to
          // hang one guard on. See `wasloai/provisioning-guard.js`.
          beforeEnter: wasloProvisionedGuard,
          component: BroadcastsIndex,
        },
        {
          path: 'broadcasts/new',
          name: 'waslo_broadcast_new',
          meta: wasloMeta,
          beforeEnter: wasloProvisionedGuard,
          component: BroadcastCompose,
        },
        {
          path: 'broadcasts/:broadcastId',
          name: 'waslo_broadcast_detail',
          meta: wasloMeta,
          beforeEnter: wasloProvisionedGuard,
          component: BroadcastDetail,
        },
        /* <<< WASLO DEVIATION <<< */
      ],
    },
  ],
};

export default campaignsRoutes;
